import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Alert,
    Chip,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FaiBox from "../../../../MuiComponents/FaiBox";
import FaiTypography from "../../../../MuiComponents/FaiTypography";
import FaiButton from "../../../../MuiComponents/FaiButton";
import OrdersController from "../../../../controllers/orderController";

interface TrackShipmentModalProps {
    open: boolean;
    onClose: () => void;
    subOrderId: string;
    shipmentId?: string | number | null;
}

interface TimelineEvent {
    activity: string;
    location: string;
    date: string;
}

interface Normalized {
    status: string;
    awb: string;
    courier: string;
    etd: string;
    origin: string;
    destination: string;
    consignee: string;
    weight: string;
    packages: string;
    pickupDate: string;
    deliveredDate: string;
    trackUrl: string;
    events: TimelineEvent[];
}

// Shiprocket's courier_company_id -> readable name. The track response returns
// the id but not always the name, so map the ones we actually ship with and
// fall back to "Courier #id" for anything else.
const COURIER_BY_ID: Record<string, string> = {
    "1": "Blue Dart Air",
    "51": "Delhivery",
    "58": "Ecom Express",
    "59": "Xpressbees",
    "62": "DTDC",
    "141": "Shadowfax",
    "169": "Amazon Shipping",
};

/**
 * The monolith returns Shiprocket's payload verbatim:
 *   { data: { tracking_data: { shipment_track: [ {...} ],
 *                              shipment_track_activities: [ {...} ],
 *                              track_url, etd } } }
 * Everything interesting sits in shipment_track[0] and the activities array —
 * not at the root. Written defensively so a camelCase or already-flattened
 * shape also works.
 */
function normalize(payload: any): Normalized | null {
    if (!payload) return null;
    const td = payload.tracking_data ?? payload.trackingData ?? payload;
    const track =
        (td.shipment_track ?? td.shipmentTrack ?? [])[0] ??
        (Array.isArray(payload.shipment_track) ? payload.shipment_track[0] : null) ??
        {};
    const rawEvents: any[] =
        td.shipment_track_activities ?? td.shipmentTrackActivities ?? td.events ?? [];

    const courierId = track.courier_company_id ?? track.courierCompanyId;
    const courier =
        track.courier_name ??
        track.courierName ??
        (courierId != null ? COURIER_BY_ID[String(courierId)] ?? `Courier #${courierId}` : "");

    return {
        status: track.current_status ?? track.currentStatus ?? "",
        awb: track.awb_code ?? track.awbCode ?? "",
        courier,
        etd: td.etd ?? track.edd ?? "",
        origin: track.origin ?? "",
        destination: track.destination ?? track.delivered_to ?? track.deliveredTo ?? "",
        consignee: track.consignee_name ?? track.consigneeName ?? "",
        weight: track.weight ?? "",
        packages: track.packages != null ? String(track.packages) : "",
        pickupDate: track.pickup_date ?? track.pickupDate ?? "",
        deliveredDate: track.delivered_date ?? track.deliveredDate ?? "",
        trackUrl: td.track_url ?? td.trackUrl ?? "",
        // Sort newest-first explicitly rather than trusting Shiprocket's array
        // order — it happens to arrive descending today but that isn't
        // documented, and the UI marks index 0 as the current status.
        // Undated entries sink to the bottom instead of corrupting the order.
        events: (rawEvents || [])
            .filter(Boolean)
            .map((e) => ({
                activity: e.activity ?? e.status ?? "",
                location: e.location ?? "",
                date: e.date ?? e.timestamp ?? "",
            }))
            .sort((a, b) => {
                const ta = Date.parse(String(a.date).replace(" ", "T"));
                const tb = Date.parse(String(b.date).replace(" ", "T"));
                if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
                if (Number.isNaN(ta)) return 1;
                if (Number.isNaN(tb)) return -1;
                return tb - ta;
            }),
    };
}

// "2026-07-26 11:00:00" -> "26 Jul, 11:00 AM"
function fmt(d: string): string {
    if (!d) return "";
    const parsed = new Date(d.replace(" ", "T"));
    if (Number.isNaN(parsed.getTime())) return d;
    return parsed.toLocaleString("en-IN", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true,
    });
}

function statusColour(status: string): "success" | "warning" | "error" | "info" {
    const s = (status || "").toLowerCase();
    if (s.includes("deliver") && !s.includes("out for")) return "success";
    if (s.includes("rto") || s.includes("cancel") || s.includes("fail") || s.includes("undeliver")) return "error";
    if (s.includes("out for") || s.includes("transit") || s.includes("dispatch")) return "warning";
    return "info";
}

const Row = ({ label, value }: { label: string; value?: string }) =>
    value ? (
        <>
            <FaiTypography variant="body2" sx={{ color: "text.secondary" }}>{label}</FaiTypography>
            <FaiTypography variant="body2" sx={{ fontWeight: 500 }}>{value}</FaiTypography>
        </>
    ) : null;

const TrackShipmentModal: React.FC<TrackShipmentModalProps> = ({
    open, onClose, subOrderId, shipmentId,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<Normalized | null>(null);

    async function load() {
        setLoading(true);
        setError(null);
        try {
            const res = await OrdersController.getDeliveryStatus(subOrderId);
            // ResponseDTO envelope: { status, data: { tracking_data: {...} } }
            setData(normalize(res?.data?.data ?? res?.data));
        } catch (err: any) {
            // A courier-side failure comes back as a server error with technical text;
            // show the merchant something they can act on instead.
            const serverFault = (err?.response?.status ?? 0) >= 500;
            setError(
                serverFault
                    ? "Tracking is not available right now. Please try again in a little while."
                    : err?.response?.data?.errors?.[0] ??
                err?.response?.data?.message ??
                err?.message ??
                "Failed to load tracking."
            );
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (open) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, subOrderId]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ pb: 1 }}>
                <FaiBox display="flex" justifyContent="space-between" alignItems="center">
                    <FaiTypography variant="h5" sx={{ fontWeight: 500 }}>Shipment tracking</FaiTypography>
                    <IconButton onClick={onClose} aria-label="close"><CloseIcon /></IconButton>
                </FaiBox>
            </DialogTitle>

            <DialogContent dividers>
                {loading && (
                    <FaiBox display="flex" alignItems="center" gap={2} py={4}>
                        <CircularProgress size={20} />
                        <FaiTypography variant="body2">Fetching latest status from Shiprocket…</FaiTypography>
                    </FaiBox>
                )}

                {error && !loading && <Alert severity="error">{error}</Alert>}

                {data && !loading && (
                    <>
                        {/* Headline status */}
                        <FaiBox display="flex" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
                            <Chip
                                label={data.status || "Awaiting update"}
                                color={statusColour(data.status)}
                                sx={{ fontWeight: 600 }}
                            />
                            {data.etd && !data.deliveredDate && (
                                <FaiTypography variant="caption" sx={{ color: "text.secondary" }}>
                                    Expected by {fmt(data.etd)}
                                </FaiTypography>
                            )}
                        </FaiBox>

                        {/* Consignment details */}
                        <FaiBox
                            display="grid"
                            gridTemplateColumns="130px 1fr"
                            gap={0.9}
                            sx={{ mb: 2, p: 1.5, borderRadius: 1, backgroundColor: "#FAFAFA" }}
                        >
                            <Row label="Courier" value={data.courier} />
                            <Row label="AWB #" value={data.awb} />
                            <Row label="Shipment ID" value={shipmentId != null ? String(shipmentId) : ""} />
                            <Row label="Route" value={data.origin && data.destination ? `${data.origin} → ${data.destination}` : data.destination} />
                            <Row label="Consignee" value={data.consignee} />
                            <Row label="Package" value={[data.weight ? `${data.weight} kg` : "", data.packages ? `${data.packages} pkg` : ""].filter(Boolean).join(" · ")} />
                            <Row label="Picked up" value={fmt(data.pickupDate)} />
                            <Row label="Delivered" value={fmt(data.deliveredDate)} />
                        </FaiBox>

                        {data.trackUrl && (
                            <FaiTypography variant="caption" sx={{ display: "block", mb: 2 }}>
                                <a href={data.trackUrl} target="_blank" rel="noreferrer" style={{ color: "#1976d2" }}>
                                    Track on Shiprocket ↗
                                </a>
                            </FaiTypography>
                        )}

                        {/* Vertical timeline — newest first */}
                        <FaiTypography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                            Timeline
                        </FaiTypography>

                        {data.events.length === 0 ? (
                            <FaiTypography variant="body2" sx={{ color: "text.secondary" }}>
                                No activity events yet — the courier hasn't scanned this shipment.
                            </FaiTypography>
                        ) : (
                            <FaiBox sx={{ position: "relative", pl: 0.5 }}>
                                {data.events.map((e, i) => {
                                    const isLatest = i === 0;
                                    const isLast = i === data.events.length - 1;
                                    return (
                                        <FaiBox
                                            key={i}
                                            sx={{ position: "relative", pl: 3.5, pb: isLast ? 0 : 2.5 }}
                                        >
                                            {/* connecting line */}
                                            {!isLast && (
                                                <FaiBox
                                                    sx={{
                                                        position: "absolute",
                                                        left: 6.5, top: 16, bottom: -2,
                                                        width: "2px",
                                                        backgroundColor: "#E0E0E0",
                                                    }}
                                                />
                                            )}
                                            {/* dot */}
                                            <FaiBox
                                                sx={{
                                                    position: "absolute",
                                                    left: 0, top: 4,
                                                    width: 15, height: 15,
                                                    borderRadius: "50%",
                                                    border: "3px solid",
                                                    borderColor: isLatest ? "#2e7d32" : "#BDBDBD",
                                                    backgroundColor: isLatest ? "#2e7d32" : "#fff",
                                                    boxShadow: isLatest ? "0 0 0 4px rgba(46,125,50,.15)" : "none",
                                                }}
                                            />
                                            <FaiTypography
                                                variant="body2"
                                                sx={{ fontWeight: isLatest ? 600 : 500, lineHeight: 1.35 }}
                                            >
                                                {e.activity}
                                            </FaiTypography>
                                            <FaiTypography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                                                {[e.location, fmt(e.date)].filter(Boolean).join("  ·  ")}
                                            </FaiTypography>
                                        </FaiBox>
                                    );
                                })}
                            </FaiBox>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <FaiButton color="info" variant="outlined" onClick={load} disabled={loading}>
                    Refresh
                </FaiButton>
                <FaiButton color="info" variant="contained" onClick={onClose}>
                    Close
                </FaiButton>
            </DialogActions>
        </Dialog>
    );
};

export default TrackShipmentModal;
